import {
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  DeleteForever,
  CheckRounded,
  MonetizationOnRounded,
  AddCircleRounded,
  ArrowBackIosRounded,
  TaskAlt,
} from "@mui/icons-material";
import ColumnContainer from "../../../common/ColumnContainer";
import {
  StyledTable,
  TableCell,
  TableHeader,
  TitleContainer,
  Wrapper,
} from "../../style";
import AutocompleteComponent from "../../../common/AutocompleteComponent";
import { useEffect, useRef, useState } from "react";
import { DashboardButton } from "../../../common/DashboardButton";
import "./ProjectStyle.less";
import RowContainer from "../../../common/RowContainer";
import { selectAuthData } from "../../../../redux/features/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";

import { userDashboardRoute } from "../../../../utils/routes/Routes";
import {
  useAddProjectMutation,
  useGetVehicleInfoEngineHpsQuery,
  useGetVehicleInfoReadingMethodsQuery,
  useGetVehicleInfoTypesQuery,
  useGetVehicleInfoBrandsQuery,
  useGetVehicleInfoEnginesQuery,
  useGetVehicleInfoGearboxesQuery,
  useGetVehicleInfoGenerationsQuery,
  useGetVehicleInfoModelsQuery,
  useGetVehicleInfoYearsQuery,
  useGetVehicleInfoEcuBrandsQuery,
  useGetVehicleInfoEcuNamesQuery,
  useGetVehicleInfoFuelsQuery,
  useGetVehicleInfoEcuTypesQuery,
} from "../../../../redux/features/projects/projectsApi";
import InfoModalComponent from "../../../common/modals/InfoModalComponent";
import { SnackbarComponent } from "../../../common/SnackbarComponent";
import UploadFileComponent from "../../../common/UploadFileComponent";
import { useGetSolutionsListQuery } from "../../../../redux/features/solutions/solutionsApi";
import { ManualVehicleInfoInsertionModal } from "./modals/ManualVehicleInfoInsertionModal";
import { useUpdateSingleUserMutation } from "../../../../redux/features/usersManagement/usersManagementApi";
import { FormattedMessage } from "react-intl";

const NewProject = () => {
  // INIT
  const fileInput = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const clearVehicleInfoState = {
    type_id: "",
    brand_id: "",
    model_id: "",
    engine_id: "",
    engine_hp_id: "",
    gearbox_id: "",
    generation_id: "",
    year_id: "",
    ecu_brand_id: "",
    ecu_name_id: "",
    ecu_type_id: "",
  };

  // MEDIA QUERIES
  const isMobile = useMediaQuery("(max-width: 500px)");
  const isTablet = useMediaQuery("(max-width: 992px)");

  // STATE
  const [openSnackbarFile, setOpenSnackbarFile] = useState(false);
  const [openSnackbarForm, setOpenSnackbarForm] = useState(false);
  const [openCreditsModal, setOpenCreditsModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openManualInfoModal, setOpenManualInfoModal] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [typeId, setTypeId] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [modelId, setModelId] = useState(null);
  const [engineId, setEngineId] = useState(null);
  const [engineHpId, setEngineHpId] = useState(null);
  const [gearboxId, setGearboxId] = useState(null);
  const [generationId, setGenerationId] = useState(null);
  const [yearId, setYearId] = useState(null);
  const [fuelId, setFuelId] = useState(null);
  const [ecuBrandId, setEcuBrandId] = useState(null);
  const [ecuNameId, setEcuNameId] = useState(null);
  const [ecuTypeId, setEcuTypeId] = useState(null);
  const [customVehicleInfo, setCustomVehicleInfo] = useState(null);
  const [readingMethodId, setReadingMethodId] = useState(null);
  const [fileUploaded, setFileUploaded] = useState(null);
  const [fileInputRef, setFileInputRef] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [selection, setSelection] = useState({
    vehicleInfo: clearVehicleInfoState,
    uploaded_file: null,
    services: [],
    dtc: [""],
    reading_method_id: "",
    custom: false,
    project_textfield: "",
  });

  // REDUX
  const { data: types } = useGetVehicleInfoTypesQuery();

  const { data: brands } = useGetVehicleInfoBrandsQuery(typeId, {
    skip: !typeId,
  });
  const { data: models } = useGetVehicleInfoModelsQuery(brandId, {
    skip: !brandId,
  });
  const { data: generations } = useGetVehicleInfoGenerationsQuery(
    {
      brandId: brandId,
      modelId: modelId,
    },
    {
      skip: !brandId || !modelId,
    }
  );
  const { data: engines } = useGetVehicleInfoEnginesQuery(
    {
      brandId: brandId,
      modelId: modelId,
      generationId: generationId,
    },
    {
      skip: !brandId || !modelId || !generationId,
    }
  );
  const { data: engineHp } = useGetVehicleInfoEngineHpsQuery(engineId, {
    skip: !engineId,
  });
  const { data: years } = useGetVehicleInfoYearsQuery(
    {
      engineId: engineId,
      engineHpId: engineHpId,
    },
    {
      skip: !engineId || !engineHpId,
    }
  );
  const { data: gearboxes } = useGetVehicleInfoGearboxesQuery();
  const { data: fuels } = useGetVehicleInfoFuelsQuery();
  const { data: readingMethods } = useGetVehicleInfoReadingMethodsQuery();

  const { data: ecuTypes } = useGetVehicleInfoEcuTypesQuery(
    {
      typeId: typeId,
      brandId: brandId,
      modelId: modelId,
      generationId: generationId,
      engineId: engineId,
      engineHpId: engineHpId,
      yearId: yearId,
      fuelId: fuelId,
      gearboxId: gearboxId,
    },
    {
      skip:
        !typeId ||
        !brandId ||
        !modelId ||
        !generationId ||
        !engineId ||
        !engineHpId ||
        !yearId ||
        !fuelId,
    }
  );

  const { data: ecuBrands } = useGetVehicleInfoEcuBrandsQuery(
    {
      typeId: typeId,
      brandId: brandId,
      modelId: modelId,
      generationId: generationId,
      engineId: engineId,
      engineHpId: engineHpId,
      yearId: yearId,
      fuelId: fuelId,
      gearboxId: gearboxId,
      ecuTypeId: ecuTypeId,
    },
    {
      skip:
        !typeId ||
        !brandId ||
        !modelId ||
        !generationId ||
        !engineId ||
        !engineHpId ||
        !yearId ||
        !fuelId ||
        !ecuTypeId,
    }
  );
  const { data: ecuNames } = useGetVehicleInfoEcuNamesQuery(
    {
      typeId: typeId,
      brandId: brandId,
      modelId: modelId,
      generationId: generationId,
      engineId: engineId,
      engineHpId: engineHpId,
      yearId: yearId,
      fuelId: fuelId,
      gearboxId: gearboxId,
      ecuTypeId: ecuTypeId,
      ecuBrandId: ecuBrandId,
    },
    {
      skip:
        !typeId ||
        !brandId ||
        !modelId ||
        !generationId ||
        !engineId ||
        !engineHpId ||
        !yearId ||
        !fuelId ||
        !ecuTypeId ||
        !ecuBrandId,
    }
  );

  const [updateUser, { isLoading: isLoadingUpdateUser }] =
    useUpdateSingleUserMutation();

  const userData = useSelector(selectAuthData);

  const {
    data: solutionsData,
    isLoading,
    isError,
  } = useGetSolutionsListQuery(
    {
      adminId: userData?.userData?.admin_id,
    },
    {
      skip: !userData?.userData?.admin_id,
    }
  );

  const [addNewProject, { isLoading: addNewProjectIsLoading }] =
    useAddProjectMutation();
  // UTILS

  const selectionOptions = [
    {
      id: "type_id",
      label: <FormattedMessage id="project.select.type" />,
      optionsData: types ?? [],
    },
    {
      id: "brand_id",
      label: <FormattedMessage id="project.select.brand" />,
      optionsData: brands ?? [],
    },
    {
      id: "model_id",
      label: <FormattedMessage id="project.select.model" />,
      optionsData: models ?? [],
    },
    {
      id: "generation_id",
      label: <FormattedMessage id="project.select.generation" />,
      optionsData: generations ?? [],
    },
    {
      id: "engine_id",
      label: <FormattedMessage id="project.select.engine" />,
      optionsData: engines ?? [],
    },
    {
      id: "engine_hp_id",
      label: <FormattedMessage id="project.select.engineHp" />,
      optionsData: engineHp ?? [],
    },
    {
      id: "year_id",
      label: <FormattedMessage id="project.select.year" />,
      optionsData: years ?? [],
    },
    {
      id: "gearbox_id",
      label: <FormattedMessage id="project.select.gearbox" />,
      optionsData: gearboxes ?? [],
    },
    {
      id: "fuel_id",
      label: <FormattedMessage id="project.select.fuel" />,
      optionsData: fuels ?? [],
    },
    {
      id: "ecu_type_id",
      label: <FormattedMessage id="project.select.ecuType" />,
      optionsData: ecuTypes ?? [],
    },
    {
      id: "ecu_brand_id",
      label: <FormattedMessage id="project.select.ecuBrand" />,
      optionsData: ecuBrands ?? [],
    },
    {
      id: "ecu_name_id",
      label: <FormattedMessage id="project.select.ecuName" />,
      optionsData: ecuNames ?? [],
    },
  ];

  const tableHeaders = [
    <FormattedMessage id="project.service" />,
    <FormattedMessage id="project.price" />,
  ];

  const manualInfoFieldsData = [
    {
      label: <FormattedMessage id="project.type" />,
      name: "type",
    },
    {
      label: <FormattedMessage id="project.brand" />,
      name: "brand",
    },
    {
      label: <FormattedMessage id="project.model" />,
      name: "model",
    },
    {
      label: <FormattedMessage id="project.generation" />,
      name: "generation",
    },
    {
      label: <FormattedMessage id="project.engine" />,
      name: "engine",
    },
    {
      label: <FormattedMessage id="project.engineHp" />,
      name: "engine_hp",
    },
    {
      label: <FormattedMessage id="project.year" />,
      name: "year",
    },
    {
      label: <FormattedMessage id="project.gearbox" />,
      name: "gearbox",
    },

    {
      label: <FormattedMessage id="project.fuel" />,
      name: "fuel",
    },
    {
      label: <FormattedMessage id="project.ecuBrand" />,
      name: "ecu_brand",
    },
    {
      label: <FormattedMessage id="project.ecuName" />,
      name: "ecu_name",
    },
    {
      label: <FormattedMessage id="project.ecuType" />,
      name: "ecu_type",
    },
  ];

  //EFFECTS
  useEffect(() => {
    setTotalPrice(calculateTotalPriceServices());
  }, [selection.services]);

  // FUNCTIONS

  const handleSelectionChange = (value, key) => {
    if (value === null) {
      value = { id: null };
    }

    const selectionOrder = [
      "type_id",
      "brand_id",
      "model_id",
      "generation_id",
      "engine_id",
      "engine_hp_id",
      "year_id",
      "gearbox_id",
      "fuel_id",
      "ecu_type_id",
      "ecu_brand_id",
      "ecu_name_id",
    ];

    setSelection((prevState) => {
      // Work on a copy of the vehicleInfo object
      let updatedVehicleInfo = { ...prevState.vehicleInfo };

      // Update the specific key for vehicleInfo selections
      if (selectionOrder.includes(key.id)) {
        updatedVehicleInfo[key.id] = value.id || null;

        // Clear all subsequent selections in the order
        const keyIndex = selectionOrder.indexOf(key.id);
        for (let i = keyIndex + 1; i < selectionOrder.length; i++) {
          updatedVehicleInfo[selectionOrder[i]] = null;
        }
      }
      let newState;
      if (key.id === "custom") {
        newState = {
          ...prevState,
          vehicleInfo: clearVehicleInfoState,
        };
      } else {
        newState = {
          ...prevState,
          vehicleInfo: updatedVehicleInfo,
        };
      }

      if (key.id === "reading_method_id") {
        newState.reading_method_id = value.id;
      }
      if (key.id === "custom") {
        newState.custom = value.target.checked;
      }

      return newState;
    });

    switch (key.id) {
      case "type_id":
        setTypeId(value ? value.id : null);
        break;
      case "brand_id":
        setBrandId(value ? value.id : null);
        break;
      case "model_id":
        setModelId(value ? value.id : null);
        break;
      case "generation_id":
        setGenerationId(value ? value.id : null);
        break;
      case "engine_id":
        setEngineId(value ? value.id : null);
        break;
      case "engine_hp_id":
        setEngineHpId(value ? value.id : null);
        break;
      case "year_id":
        setYearId(value ? value.id : null);
        break;
      case "gearbox_id":
        setGearboxId(value ? value.id : null);
        break;
      case "fuel_id":
        setFuelId(value ? value.id : null);
        break;
      case "reading_method_id":
        setReadingMethodId(value ? value.id : null);
        break;
      case "custom":
        setCustomVehicleInfo(value.target.checked);
        break;
      case "ecu_brand_id":
        setEcuBrandId(value ? value.id : null);
        break;
      case "ecu_name_id":
        setEcuNameId(value ? value.id : null);
        break;
      case "ecu_type_id":
        setEcuTypeId(value ? value.id : null);
        break;
      default:
        break;
    }
  };

  const getSelectValue = (option) => {
    const objSelected =
      option.optionsData.length > 0
        ? option.optionsData.find(
            (apiObj) => apiObj.id === selection?.vehicleInfo[option.id]
          )
        : null;
    return objSelected;
  };

  const handleFileChange = (file) => {
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (["zip", "bin", "mod"].includes(fileExtension)) {
        setSelection((prevState) => ({
          ...prevState,
          uploaded_file: file,
        }));
      } else {
        setOpenSnackbarFile(true);
      }
    }
  };

  const handleFileUpload = (fileUploaded) => {
    setFileUploaded(fileUploaded);
  };

  const handleFileInputRefChange = (fileInput) => {
    setFileInputRef(fileInput);
  };

  const handleFileSizeError = (isError) => {
    setFileSizeError(isError);
  };

  const handleDeleteFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelection((prevState) => {
      return {
        ...prevState,
        uploaded_file: null,
      };
    });
    if (fileInput.current) {
      fileInput.current.value = "";
    }
  };

  const handleCheckboxChange = (e, solution) => {
    if (e.target.checked) {
      setSelection((prevState) => {
        return {
          ...prevState,
          services: [...prevState.services, solution],
        };
      });
    } else {
      setSelection((prevState) => {
        return {
          ...prevState,
          services: [
            ...prevState.services.filter(
              (service) => service.id !== solution.id
            ),
          ],
        };
      });
    }
  };

  const handleAddNewDTCField = () => {
    setSelection((prevState) => {
      return {
        ...prevState,
        dtc: [...prevState.dtc, ""],
      };
    });
  };

  const handleDeleteDTCField = (index) => {
    setSelection((prevState) => {
      return {
        ...prevState,
        dtc: [...prevState.dtc.filter((_, i) => i !== index)],
      };
    });
    if (selection.dtc.length === 1) {
      setSelection((prevState) => {
        return {
          ...prevState,
          vehicleInfo: {
            ...prevState.vehicleInfo,
            dtc: [""],
          },
        };
      });
    }
  };

  const calculateTotalPriceServices = () => {
    if (selection?.services && selection.services.length > 0) {
      return selection.services.reduce((acc, curr) => {
        return acc + curr.price;
      }, 0);
    } else {
      return 0;
    }
  };

  const renderServicesSelectedRows = () => {
    return (
      <>
        {selection.services.map((service, index) => (
          <tr key={index}>
            <TableCell>{service.name}</TableCell>
            <TableCell>
              <Chip
                label={
                  <RowContainer minGap start align="center">
                    <MonetizationOnRounded
                      sx={{ "& path": { color: "var(--main-color)" } }}
                    />{" "}
                    <span>{service.price}</span>
                  </RowContainer>
                }
              />
            </TableCell>
          </tr>
        ))}
        <tr>
          <TableCell>
            <Typography variant="p" component="p" color="var(--main-color)">
              <FormattedMessage id="project.total" />
            </Typography>
          </TableCell>{" "}
          <TableCell>
            <Chip
              sx={{ backgroundColor: "var(--main-color)" }}
              label={
                <RowContainer minGap start align="center">
                  <MonetizationOnRounded />{" "}
                  <span>{calculateTotalPriceServices()}</span>
                </RowContainer>
              }
            />
          </TableCell>
        </tr>
      </>
    );
  };

  const handleOpenCreditsModal = () => {
    setOpenCreditsModal(true);
  };
  const handleCloseCreditsModal = () => {
    setOpenCreditsModal(false);
  };
  const handleOpenConfirmModal = () => {
    setOpenConfirmModal(true);
  };
  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false);
  };
  const handleOpenManualInfoModal = () => {
    setOpenManualInfoModal(true);
  };
  const handleCloseManualInfoModal = () => {
    setOpenManualInfoModal(false);
  };

  const handleConfirm = async (event) => {
    if (!!selection.custom) {
      setSelection((prevState) => {
        return {
          ...prevState,
          vehicleInfo: clearVehicleInfoState,
        };
      });
    }

    if (totalPrice > userData?.userData?.credits) {
      handleOpenCreditsModal();
    } else {
      if (
        (selection.custom &&
          selection.project_textfield &&
          selection.reading_method_id &&
          selection.uploaded_file &&
          selection.services.length > 0) ||
        (selection.vehicleInfo.type_id &&
          selection.vehicleInfo.brand_id &&
          selection.vehicleInfo.model_id &&
          selection.vehicleInfo.engine_id &&
          selection.vehicleInfo.engine_hp_id &&
          selection.vehicleInfo.gearbox_id &&
          selection.vehicleInfo.generation_id &&
          selection.vehicleInfo.year_id &&
          selection.reading_method_id &&
          selection.vehicleInfo.ecu_brand_id &&
          selection.vehicleInfo.ecu_name_id &&
          selection.vehicleInfo.ecu_type_id &&
          selection.reading_method_id &&
          selection.uploaded_file &&
          selection.services.length > 0)
      ) {
        handleOpenConfirmModal();
      } else {
        setOpenSnackbarForm(true);
      }
    }
  };
  const handleSaveManualInfo = (manualInfo) => {
    const objToString = (manualInfo) => {
      return Object.keys(manualInfo)
        .map((key) => manualInfo[key])
        .join(", ");
    };

    setSelection((prevState) => {
      return {
        ...prevState,
        project_textfield: objToString(manualInfo),
      };
    });
  };

  const handleFinalConfirm = async (event) => {
    try {
      const formData = new FormData();
      if (!selection.custom) {
        Object.keys(selection.vehicleInfo).forEach((key) => {
          formData.append(key, selection.vehicleInfo[key]);
        });
      }
      const serviceNameArrayToString = selection?.services
        ?.map((service) => service.name)
        .join(", ");

      formData.append("services", serviceNameArrayToString);
      formData.append("reading_method_id", selection.reading_method_id);
      formData.append("file", selection.uploaded_file);

      if (selection.dtc) {
        const dtcArrayToString = selection.dtc.join(", ");
        formData.append("dtc", dtcArrayToString);
      }
      if (selection.custom && selection.project_textfield) {
        formData.append("project_textfield", selection.project_textfield);
      }

      const response = await addNewProject({
        userId: userData?.userData?.id,
        formData: formData,
      }).unwrap();

      const projectId = response.id;
      event.preventDefault();
      event.stopPropagation();
      const totalCreditsUsed = calculateTotalPriceServices();
      const newCreditsBalance = userData?.userData?.credits - totalCreditsUsed;
      await updateUser({
        userId: userData?.userData?.id,
        credits: newCreditsBalance,
      });

      navigate(`${userDashboardRoute.projects}/${projectId}`);
    } catch (err) {
      console.log("err", err);
    }
  };

  const handleGoBackToProjects = () => {
    navigate(`${userDashboardRoute.projects}`);
  };

  const handleDtcFieldChange = (e, index) => {
    const value = e.target.value;

    setSelection((prevState, i) => {
      return {
        ...prevState,
        dtc:
          prevState.dtc.length > 0
            ? prevState.dtc.map((item, i) => (i === index ? value : item))
            : [value],
      };
    });
  };

  return (
    <>
      {projectId ? (
        <Outlet />
      ) : (
        <Wrapper className="new-project">
          <ColumnContainer style={{ gap: "3rem" }} start>
            <ColumnContainer>
              <TitleContainer>
                <ColumnContainer noGap>
                  <RowContainer start align="center">
                    <RowContainer minGap start align="center">
                      <IconButton>
                        <ArrowBackIosRounded
                          onClick={() => handleGoBackToProjects()}
                        />
                      </IconButton>
                      <Typography variant="h3" component="h3">
                        <FormattedMessage id="project.new" />
                      </Typography>
                    </RowContainer>
                  </RowContainer>
                </ColumnContainer>
              </TitleContainer>
            </ColumnContainer>

            <Grid container spacing={6}>
              {selectionOptions.length > 0 &&
                selectionOptions.map((option) => {
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      lg={12}
                      height={"fit-content"}
                      key={option.id}
                    >
                      <AutocompleteComponent
                        disabled={!!selection.custom}
                        value={getSelectValue(option)}
                        onChange={(e, value) =>
                          handleSelectionChange(value, { id: option.id })
                        }
                        options={option.optionsData || []}
                        getOptionLabel={(option) =>
                          option?.name || option.engine_cv || ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required={customVehicleInfo ? false : true}
                            variant="filled"
                            label={option.label}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                  );
                })}
            </Grid>
            <ColumnContainer>
              <RowContainer start minGap align="center">
                <Checkbox
                  value={selection?.custom}
                  onChange={(e) => handleSelectionChange(e, { id: "custom" })}
                ></Checkbox>
                <Typography
                  variant="p"
                  color="var(--text-primary)"
                  marginTop={"3px"}
                >
                  <FormattedMessage id="project.select.notFound" />
                </Typography>
              </RowContainer>

              <RowContainer start align="center">
                <DashboardButton
                  type="filled"
                  sx={{ width: "fit-content" }}
                  onClick={() => handleOpenManualInfoModal()}
                  disabled={!selection.custom}
                >
                  <FormattedMessage id="project.insert.vehicleInfo" />
                </DashboardButton>
              </RowContainer>
            </ColumnContainer>
            <Grid container spacing={6}>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                height={"fit-content"}
                marginTop={"20px"}
              >
                <AutocompleteComponent
                  value={readingMethods?.find(
                    (item) => item.id === selection.reading_method_id || ""
                  )}
                  onChange={(e, value) =>
                    handleSelectionChange(value, { id: "reading_method_id" })
                  }
                  options={readingMethods}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      variant="filled"
                      label={
                        <FormattedMessage id="project.select.readingMethod" />
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <RowContainer className="upload-file-container">
              <Grid container>
                <Grid item xs={12} sm={12} padding={"20px"}>
                  <ColumnContainer>
                    <RowContainer center>
                      <UploadFileComponent
                        acceptedFormatsString={".zip, .mod, .bin"}
                        onFileUpload={handleFileUpload}
                        onFileChange={handleFileChange}
                        onFileInputRefChange={handleFileInputRefChange}
                        onFileSizeError={handleFileSizeError}
                        instructions={
                          <FormattedMessage id="file.attach.user" />
                        }
                      >
                        {selection?.uploaded_file ? (
                          <>
                            <RowContainer
                              className="success-upload-icons"
                              center
                            >
                              <RowContainer center minGap align="end">
                                <CheckRounded />
                                <Typography component="p" fontSize={"16px"}>
                                  {selection?.uploaded_file?.name}
                                </Typography>
                              </RowContainer>

                              <RowContainer minGap align="center">
                                <DeleteForever
                                  onClick={(e) => handleDeleteFile(e)}
                                />
                              </RowContainer>
                            </RowContainer>
                          </>
                        ) : fileSizeError ? (
                          <Typography
                            component="p"
                            fontSize={"16px"}
                            color={"var(--error)"}
                            textAlign={"center"}
                          >
                            {<FormattedMessage id="file.size.error" />}
                          </Typography>
                        ) : (
                          <Typography
                            component="p"
                            fontSize={"16px"}
                            textAlign="center"
                            color="var(--error)"
                          >
                            <FormattedMessage id="file.noFile" />
                          </Typography>
                        )}
                      </UploadFileComponent>
                    </RowContainer>
                  </ColumnContainer>
                </Grid>
              </Grid>
            </RowContainer>
            <ColumnContainer>
              <TitleContainer>
                <Typography variant="h5" component="h5">
                  <FormattedMessage id="project.services" />*
                </Typography>
              </TitleContainer>
              <FormControl component="fieldset">
                <FormGroup>
                  {isLoading ? (
                    <Typography variant="h6" component="h6">
                      Loading...
                    </Typography>
                  ) : isError ? (
                    <Typography variant="h6" component="h6">
                      Error
                    </Typography>
                  ) : solutionsData ? (
                    <Grid container columnSpacing={12} rowSpacing={2}>
                      {solutionsData.map((solution, index) => (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={6}
                          lg={6}
                          key={solution.id}
                        >
                          <RowContainer
                            align="center"
                            style={{ width: "65%" }}
                            className="solution-item"
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  onChange={(e) =>
                                    handleCheckboxChange(e, solution)
                                  }
                                  color="primary"
                                  value={""}
                                />
                              }
                              label={
                                <Typography variant="p">
                                  {solution.name}
                                </Typography>
                              }
                            />
                            <Chip
                              label={
                                <RowContainer minGap start align="center">
                                  <MonetizationOnRounded />{" "}
                                  <span>{solution.price}</span>
                                </RowContainer>
                              }
                            />
                          </RowContainer>
                        </Grid>
                      ))}
                    </Grid>
                  ) : null}
                </FormGroup>
              </FormControl>
              <Divider />
              <RowContainer end style={{ width: "90%" }}>
                <Typography
                  variant="p"
                  component="p"
                  sx={{ color: "var(--main-color)" }}
                >
                  <FormattedMessage id="project.total.services" />:{" "}
                  {totalPrice > 0 ? totalPrice : 0}{" "}
                  <FormattedMessage id="credits" />
                </Typography>
              </RowContainer>
              {selection.services.some((sol) => sol.name === "DTC") ? (
                <ColumnContainer minGap>
                  <TitleContainer>
                    <Typography variant="h5" component="h5">
                      <FormattedMessage id="project.dtc.achronym" />
                    </Typography>
                  </TitleContainer>
                  <Typography variant="p" component="p">
                    <FormattedMessage id="project.dtc.select" />
                  </Typography>
                  {selection.dtc.map((dtc, index) => (
                    <RowContainer start halfGap align="center" key={index}>
                      <TextField
                        value={dtc || ""}
                        onChange={(e) => handleDtcFieldChange(e, index)}
                        autoComplete="off"
                      />
                      <AddCircleRounded
                        onClick={handleAddNewDTCField}
                        sx={{
                          "& path": {
                            color: "var(--main-color)",
                            cursor: "pointer",
                          },
                        }}
                      />
                      <DeleteForever
                        onClick={() => handleDeleteDTCField(index)}
                        sx={{
                          "& path": {
                            color: "var(--main-color)",
                            cursor: "pointer",
                          },
                        }}
                      />
                    </RowContainer>
                  ))}
                </ColumnContainer>
              ) : null}
              <SnackbarComponent
                open={openSnackbarFile}
                duration={8000}
                handleClose={() => setOpenSnackbarFile(false)}
                message={<FormattedMessage id="snackbar.file.format" />}
              />
              <SnackbarComponent
                open={openSnackbarForm}
                duration={8000}
                handleClose={() => setOpenSnackbarForm(false)}
                message={<FormattedMessage id="snackbar.form" />}
              ></SnackbarComponent>
              <RowContainer end className="continue-btn">
                <DashboardButton
                  type="filled"
                  sx={{ width: "fit-content" }}
                  onClick={(event) => handleConfirm(event)}
                >
                  <FormattedMessage id="button.confirm" />
                </DashboardButton>
              </RowContainer>
            </ColumnContainer>
          </ColumnContainer>

          <InfoModalComponent
            openModal={openCreditsModal}
            handleCloseModal={handleCloseCreditsModal}
          >
            <ColumnContainer align="center">
              <Typography variant="p" component="p">
                <FormattedMessage id="project.insert.vehicleInfo" />{" "}
                {calculateTotalPriceServices()}{" "}
                <FormattedMessage id="credits" />.
              </Typography>
              <Typography variant="p" component="p">
                <FormattedMessage id="project.new.modal.missing.start" />
                {totalPrice - userData?.userData?.credits}{" "}
                <FormattedMessage id="project.new.modal.missing.end" />
              </Typography>
              <Typography variant="p" component="p">
                <FormattedMessage id="project.new.modal.continue" />
              </Typography>
              <Link
                to={userDashboardRoute.buyCredits}
                style={{ display: "block" }}
              >
                <Typography
                  variant="p"
                  component="p"
                  sx={{
                    color: "var(--main-color)",
                    textDecoration: "underline",
                  }}
                >
                  <FormattedMessage id="project.new.modal.buy" />
                </Typography>
              </Link>
            </ColumnContainer>
          </InfoModalComponent>

          <InfoModalComponent
            className={["final-confirm"]}
            openModal={openConfirmModal}
            handleCloseModal={handleCloseConfirmModal}
            confirmButton={true}
            handleConfirm={handleFinalConfirm}
          >
            <ColumnContainer align="start" style={{ gap: "4rem" }}>
              <Typography variant="p" component="p" paddingLeft="8px">
                <FormattedMessage id="project.new.modal.services" />
              </Typography>

              <StyledTable>
                <thead>
                  <tr>
                    {tableHeaders.map((header, index) => (
                      <TableHeader key={index}>{header}</TableHeader>
                    ))}
                  </tr>
                </thead>
                <tbody>{renderServicesSelectedRows()}</tbody>
              </StyledTable>
              <RowContainer className="total-credits-used">
                N. {calculateTotalPriceServices()}{" "}
                <FormattedMessage id="credits" />{" "}
                <FormattedMessage id="project.modal.credits" />
              </RowContainer>
            </ColumnContainer>
          </InfoModalComponent>
          <ManualVehicleInfoInsertionModal
            formData={manualInfoFieldsData}
            openManualInfoModal={openManualInfoModal}
            handleCloseManualInfoModal={handleCloseManualInfoModal}
            onPassManualInfo={handleSaveManualInfo}
          />
        </Wrapper>
      )}
    </>
  );
};
export default NewProject;
